// AttendeesListPage — список участников события (отдельная страница).
// Используется при переходе по deep-link /calendar/events/:id/attendees.
// R41 / U-15: имена пользователей резолвятся через useUserMap (email-first цепочка),
// сырой UUID в subtitle и колонке «User ID» устранён.
// R41 / U-16: статус участника — локализованная подпись (calendar.attendee.*).
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from '@mui/material';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { AttendeeAvatar } from './components/AttendeeAvatar';
import { calendarApi } from '@/api/calendar';
import { useUserMap, resolveUserName } from '@/api/users';
import { t } from '@/i18n';
import { formatAttendeeStatus } from '@/lib/format';

/** Список участников события. */
export function AttendeesListPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['calendar-event-attendees', id],
    queryFn: () => calendarApi.listAttendees(id ?? ''),
    enabled: Boolean(id),
  });

  // R41 / U-15: резолв имён участников по ids (lookup isAuthenticated, email-first цепочка).
  const attendeeIds = query.data?.map((a) => a.userId) ?? [];
  const userMap = useUserMap(attendeeIds);

  return (
    <Box>
      <PageHeader
        title="Участники события"
        actions={null}
      />
      {query.isLoading ? (
        <LoadingSpinner />
      ) : query.isError ? (
        <EmptyState
          title="Не удалось загрузить участников"
          description={query.error instanceof Error ? query.error.message : 'Неизвестная ошибка'}
          actionLabel="Назад"
          onAction={() => navigate(-1)}
        />
      ) : !query.data || query.data.length === 0 ? (
        <EmptyState title="Нет участников" />
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Пользователь</TableCell>
                  <TableCell>Статус</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <AttendeeAvatar attendee={a} size={32} />
                      </Stack>
                    </TableCell>
                    <TableCell>{resolveUserName(userMap, a.userId)}</TableCell>
                    <TableCell>{formatAttendeeStatus(a.status, t)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
